import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Container,
  Paper,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Login as LoginIcon,
} from '@mui/icons-material';

const EmailVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationStatus, setVerificationStatus] = useState('loading'); // 'loading', 'success', 'error'
  const [message, setMessage] = useState('');
  const [, setUser] = useState(null);
  const hasVerified = useRef(false);

  useEffect(() => {
    const verifyEmail = async () => {
      // Prevent multiple verification attempts
      if (hasVerified.current) {
        return;
      }

      const token = searchParams.get('token');
      
      if (!token) {
        setVerificationStatus('error');
        setMessage('No verification token provided');
        hasVerified.current = true;
        return;
      }

      // Mark as verified attempt to prevent duplicate calls
      hasVerified.current = true;

      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/user-auth/verify-email?token=${token}`);
        const data = await response.json();

        if (data.success) {
          setVerificationStatus('success');
          setMessage(data.message);
          setUser(data.user);
        } else {
          setVerificationStatus('error');
          setMessage(data.error);
        }
      } catch (error) {
        setVerificationStatus('error');
        setMessage('Failed to verify email. Please try again.');
        console.error('Email verification error:', error);
      }
    };

    verifyEmail();
  }, [searchParams]);

  const handleGoToQuiz = () => {
    navigate('/quiz');
  };


  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        {verificationStatus === 'loading' && (
          <Box>
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h5" sx={{ mb: 2 }}>
              Verifying your email...
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Please wait while we verify your email address.
            </Typography>
          </Box>
        )}

        {verificationStatus === 'success' && (
          <Box>
            <CheckCircleIcon 
              sx={{ 
                fontSize: 80, 
                color: 'success.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h4" sx={{ mb: 2, color: 'success.main' }}>
              Email Verified!
            </Typography>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Welcome to Mind Your Music!
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              Your email has been successfully verified. You can now log in and start playing quizzes!
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<LoginIcon />}
                onClick={handleGoToQuiz}
                sx={{ minWidth: 150 }}
              >
                Go to Login
              </Button>
            </Box>
          </Box>
        )}

        {verificationStatus === 'error' && (
          <Box>
            <ErrorIcon 
              sx={{ 
                fontSize: 80, 
                color: 'error.main', 
                mb: 2 
              }} 
            />
            <Typography variant="h4" sx={{ mb: 2, color: 'error.main' }}>
              Verification Failed
            </Typography>
            <Alert severity="error" sx={{ mb: 3 }}>
              {message}
            </Alert>
            <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
              The verification link may have expired or is invalid. Please try registering again or contact support.
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/quiz')}
                sx={{ minWidth: 150 }}
              >
                Go Back to Quiz
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default EmailVerificationPage;
